<template>
  <div class="bg-[#F2F6FA] min-h-screen w-full">
    <div class="flex flex-col w-full pb-20 py-4">
      <section class="w-full">
        <!-- Page Header -->
        <div class="flex items-center justify-between md:mt-5 mb-2 md:mb-0 mt-2 px-4">
          <div class="flex justify-between items-center w-full">
            <div>
              <div class="items-center gap-2 d-flex">
                <img
                  src="@/assets/img/sidebar/icons8-rank-64.png"
                  class="cursor-pointer w-6 md:w-[28px]"
                  alt="analysis"
                />
                <div class="flex items-center gap-1">
                  <p class="text-base md:text-[22px] font-bold">
                    Competitor Analysis
                    <sup class="text-sm text-gray-500 pl-2">Beta</sup>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Page Description -->
        <div class="flex items-center justify-start mb-3 px-4">
          <div class="items-center gap-2 d-flex">
            <div class="flex items-center gap-1">
              <p class="text-lg md:text-[18px] text-gray-600">
                Compare your business with competitors for comprehensive insights
              </p>
            </div>
          </div>
        </div>

        <v-tabs v-model="activeTab" background-color="transparent" color="primary" grow>
          <v-tab>Competitor Analysis</v-tab>
          <v-tab>Analysis History</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="w-full">
          <v-tab-item class="w-full">
            <!-- Loading State -->
            <div v-if="loading && stores.length === 0" class="min-h-[70vh] w-full flex flex-col items-center justify-center">
              <v-progress-circular indeterminate color="primary" size="64" width="6"></v-progress-circular>
              <p class="mt-6 text-xl text-gray-700">Loading locations...</p>
            </div>

            <!-- No Locations State -->
            <div v-else-if="stores.length === 0 && !loading" class="text-center py-8 w-full px-4">
              <div class="p-8 bg-white rounded-lg shadow-sm max-w-3xl mx-auto text-center">
                <NoLocationsConnected
                  message="Connect your Google Business locations with Fly to access competitor analysis. Once connected, you'll be able to compare your business with competitors."
                  actionText="Connect Google Locations"
                  @click.native="handleConnectGMB"
                />
              </div>
            </div>

            <!-- Selection Form or Report Content -->
            <div v-else class="w-full min-h-[calc(100vh-200px)] flex flex-col items-center justify-center px-4">
              <!-- Selection Form -->
              <div v-if="!comparisonData && !activeHistoryReport && !marketReportData" 
                class="w-full max-w-full p-6 md:p-8 bg-white rounded-lg shadow-md mx-auto form-container flex flex-col justify-center">
                
                <!-- ADD Comparison Mode Selection -->
                <div class="mb-6 md:mb-8">
                  <label class="block text-gray-700 font-medium mb-3">Select Comparison Type</label>
                  <v-radio-group v-model="comparisonMode" row @change="handleModeChange">
                    <v-radio label="Head to Head Comparison" value="headToHead"></v-radio>
                    <v-radio label="Multiple Competitors Comparison" value="multiple"></v-radio>
                  </v-radio-group>
                </div>

                <div class="text-center mb-8">
                  <h2 class="text-2xl font-bold text-gray-800 mb-2">Compare Your Business</h2>
                  <p class="text-gray-600">Select your location and a competitor to generate a comprehensive analysis</p>
                </div>

                <!-- Select Your Location -->
                <div class="mb-8 md:mb-12 relative">
                  <label class="block text-gray-700 font-medium mb-3">Select Your Location</label>
                  <div class="relative w-full flex-1">
                    <div @click="toggleStoreDropdown($event)" class="relative">
                      <v-text-field 
                        v-model="locationSearch" 
                        :placeholder="selectedStore ? `${selectedStore.gmbProfile?.title || selectedStore.name}` : 'Search and select location'"
                        hide-details 
                        dense 
                        outlined 
                        class="location-input"
                        :error="showValidation && !selectedStore"
                        color="rgba(0, 0, 0, 0.6)" 
                        background-color="white"
                        prepend-inner-icon="mdi-map-marker"
                        @click.stop="openDropdown"
                        @input="handleSearchInput"
                        @focus="openDropdown"
                      >
                        <template v-slot:append>
                          <v-icon>{{ showDropdown ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                        </template>
                      </v-text-field>

                      <div ref="showStoreDropdown" v-if="showDropdown"
                        class="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-2xl z-[9999] max-h-[400px] w-full overflow-y-auto">
                        <div class="p-2">
                          <div v-for="profile in filteredStores" :key="profile.wid"
                            class="platform p-4 hover:bg-gray-100 cursor-pointer rounded-md mb-2 border border-gray-100" 
                            @click.stop="selectStore(profile)"
                            :class="{ 'bg-blue-50 border border-blue-200': selectedWID === profile.wid }">
                            <div class="flex items-start gap-3">
                              <v-avatar size="40" color="grey lighten-3" class="mt-1">
                                <v-icon color="grey darken-2">mdi-store</v-icon>
                              </v-avatar>
                              <div class="flex flex-col w-full">
                                <span class="font-medium text-base text-gray-900">
                                  {{ profile.gmbProfile?.title || profile.name }}
                                </span>
                                <div class="flex flex-wrap gap-1 mt-1" v-if="profile.gmbProfile?.category">
                                  <span class="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
                                    {{ profile.gmbProfile.category }}
                                  </span>
                                </div>
                                <div class="mt-2">
                                  <div class="text-sm text-gray-600 flex items-start gap-1">
                                    <v-icon small class="mt-0.5" color="gray">mdi-map-marker</v-icon>
                                    <span>
                                      {{ profile.gmbProfile?.address || profile.sub_locality || profile.address || 'No address available' }}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-if="filteredStores.length === 0" class="p-3 text-center text-gray-500">
                            No locations match your search
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Selected Location Details -->
                  <div v-if="selectedStore" class="mt-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                    <div class="flex items-start gap-3">
                      <v-avatar size="40" color="primary lighten-4" class="mt-1">
                        <v-icon color="primary">mdi-store</v-icon>
                      </v-avatar>
                      <div class="flex-1">
                        <div class="font-medium text-lg text-gray-900">{{ selectedStore.gmbProfile?.title || selectedStore.name }}</div>
                        <div class="text-sm text-gray-600 mt-2">
                          <div class="flex items-start gap-2">
                            <v-icon small class="mt-0.5" color="gray">mdi-map-marker</v-icon>
                            <span>{{ selectedStore.gmbProfile?.address || selectedStore.sub_locality || selectedStore.address }}</span>
                          </div>
                        </div>
                        <div class="flex flex-wrap gap-2 mt-3">
                          <span v-if="selectedStore.gmbProfile?.category" class="text-xs px-2 py-1 bg-primary bg-opacity-10 rounded-full text-primary">
                            {{ selectedStore.gmbProfile.category }}
                          </span>
                          <span v-if="selectedStore.gmbProfile?.reviewsCount" class="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600 flex items-center gap-1">
                            <v-icon x-small color="amber darken-2">mdi-star</v-icon>
                            {{ selectedStore.gmbProfile.rating || '0' }}
                            ({{ selectedStore.gmbProfile.reviewsCount }} reviews)
                          </span>
                        </div>
                      </div>
                      <v-btn icon small color="gray" @click.stop="clearSelectedStore" class="mt-1">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
                
                <!-- Select Competitor -->
                <div v-if="comparisonMode === 'headToHead'" class="mb-8 md:mb-12">
                  <label class="block text-gray-700 font-medium mb-3">Select Competitor</label>
                  <div class="relative w-full">
                    <input
                      ref="competitorAutocomplete"
                      type="text"
                      placeholder="Search for a competitor nearby"
                      class="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      :disabled="!selectedStore"
                      autocomplete="off"
                    />
                    <div v-if="!selectedStore" class="text-xs text-gray-500 mt-2">
                      Please select your location first
                    </div>
                  </div>
                  <div v-if="selectedCompetitor" class="mt-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                    <div class="font-medium">{{ selectedCompetitor.name }}</div>
                    <div class="text-sm text-gray-600">{{ selectedCompetitor.address }}</div>
                  </div>
                </div>
                
                <!-- Generate Report Button -->
                <div class="flex justify-center mt-4">
                  <v-btn
                    color="primary"
                    :disabled="generateButtonDisabled"
                    @click="generateReport"
                    :loading="isLoading"
                    x-large
                    class="px-8"
                  >
                    Generate Report
                  </v-btn>
                </div>
              </div>

              <!-- Report Content -->
              <div v-else class="w-full mt-6 report-content-wrapper">
                <!-- Report Header with Actions -->
                <div class="bg-white shadow-sm rounded-lg p-4 md:p-6 mb-6 sticky top-0 z-10">
                  <div class="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                    <div class="flex items-center gap-4">
                      <h2 class="text-xl font-bold">Comparison Report</h2>
                      <p v-if="currentReportStatus" class="text-sm text-gray-600">{{ currentReportStatus }}</p>
                    </div>
                    
                    <div class="flex flex-wrap items-center gap-2">
                      <v-btn v-if="comparisonData && !activeHistoryReport && (selectedStore && selectedCompetitor)" 
                            color="secondary"
                            small
                            @click="generateReport(true)" 
                            :loading="isLoading">
                        <v-icon left small>mdi-refresh</v-icon>
                        Rerun
                      </v-btn>
                      
                      <v-btn v-if="activeHistoryReport && comparisonData" 
                            outlined
                            small
                            color="primary"
                            @click="clearHistoryView">
                        Return to Current
                      </v-btn>
                      
                      <v-btn outlined color="primary" small @click="resetFormAndSwitchTab">
                        <v-icon left small>mdi-plus</v-icon>
                        New
                      </v-btn>
                      
                      <v-btn color="primary" small @click="downloadReport">
                        <v-icon left small>mdi-download</v-icon>
                        Download
                      </v-btn>
                    </div>
                  </div>
                </div>

                <!-- HEAD TO HEAD REPORT DISPLAY (Existing) -->
                <template v-if="comparisonMode === 'headToHead' && (comparisonData || activeHistoryReport)">
                  <!-- Sticky Profile Names and Overall Scores -->
                  <div class="bg-white shadow-sm rounded-lg p-4 md:p-6 mb-6 sticky top-[84px] z-10 overflow-x-auto">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 min-w-[600px]">
                      <div class="flex flex-col">
                        <div class="flex items-center justify-between mb-2">
                          <h3 class="text-lg font-semibold text-primary">{{ profile1?.title || 'Your Business' }}</h3>
                          <span class="text-xl md:text-2xl font-bold text-primary">SEO Score: {{ profile1?.scores?.overall?.toFixed(2) || '0.00' }}</span>
                        </div>
                        <div class="text-sm text-gray-600 truncate" :title="profile1?.address || 'No address available'">{{ profile1?.address || 'No address available' }}</div>
                        <div class="text-sm text-gray-600 flex items-center gap-2">
                          <span v-if="profile1?.reviewsCount">
                            {{ profile1.reviewsCount }} reviews
                          </span>
                          <span v-if="profile1?.totalScore">
                            ({{ profile1.totalScore }} stars)
                          </span>
                        </div>
                        <v-progress-linear
                          :value="(profile1?.scores?.overall || 0) * 10"
                          height="4"
                          rounded
                          class="mt-2"
                          color="primary"
                        ></v-progress-linear>
                      </div>
                      <div class="flex flex-col">
                        <div class="flex items-center justify-between mb-2">
                          <h3 class="text-lg font-semibold text-orange-600">{{ profile2?.title || 'Competitor' }}</h3>
                          <span class="text-xl md:text-2xl font-bold text-orange-600">SEO Score: {{ profile2?.scores?.overall?.toFixed(2) || '0.00' }}</span>
                        </div>
                        <div class="text-sm text-gray-600 truncate" :title="profile2?.address || 'No address available'">{{ profile2?.address || 'No address available' }}</div>
                        <div class="text-sm text-gray-600 flex items-center gap-2">
                          <span v-if="profile2?.reviewsCount">
                            {{ profile2.reviewsCount }} reviews
                          </span>
                          <span v-if="profile2?.totalScore">
                            ({{ profile2.totalScore }} stars)
                          </span>
                        </div>
                        <v-progress-linear
                          :value="(profile2?.scores?.overall || 0) * 10"
                          height="4"
                          rounded
                          class="mt-2"
                          color="orange"
                        ></v-progress-linear>
                      </div>
                    </div>
                  </div>

                  <!-- NEW: SEO Analysis Summary -->
                  <div v-if="aiAnalysis?.audit?.ai_analysis" class="col-span-12 bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
                    <h3 class="text-xl font-bold mb-4 text-gray-800">SEO Analysis Summary</h3>
                    <p class="text-gray-700 whitespace-pre-line">{{ aiAnalysis.audit.ai_analysis }}</p>
                  </div>

                  <!-- NEW: SWOT Analysis -->
                  <div v-if="aiAnalysis?.audit" class="col-span-12 bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
                    <h3 class="text-xl font-bold mb-4 text-center text-gray-800">SWOT Analysis for {{ profile1?.title || 'Your Business' }}</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <!-- Strengths -->
                      <div class="bg-green-50 p-4 rounded-lg shadow">
                        <h4 class="font-bold text-lg text-green-700 mb-3 flex items-center">
                          <v-icon color="green" class="mr-2">mdi-thumb-up-outline</v-icon> Strengths
                        </h4>
                        <ul v-if="aiAnalysis.audit.strengths && aiAnalysis.audit.strengths.length" class="list-disc list-inside space-y-1 pl-5 text-gray-700">
                          <li v-for="(item, index) in aiAnalysis.audit.strengths" :key="`swot-strength-${index}`">{{ item }}</li>
                        </ul>
                        <p v-else class="text-gray-500 itaic">No specific strengths identified.</p>
                      </div>
                      <!-- Weaknesses -->
                      <div class="bg-red-50 p-4 rounded-lg shadow">
                        <h4 class="font-bold text-lg text-red-700 mb-3 flex items-center">
                          <v-icon color="red" class="mr-2">mdi-thumb-down-outline</v-icon> Weaknesses
                        </h4>
                        <ul v-if="aiAnalysis.audit.weaknesses && aiAnalysis.audit.weaknesses.length" class="list-disc list-inside space-y-1 pl-5 text-gray-700">
                          <li v-for="(item, index) in aiAnalysis.audit.weaknesses" :key="`swot-weakness-${index}`">{{ item }}</li>
                        </ul>
                        <p v-else class="text-gray-500 itaic">No specific weaknesses identified.</p>
                      </div>
                      <!-- Opportunities (from recommendations) -->
                      <div class="bg-blue-50 p-4 rounded-lg shadow">
                        <h4 class="font-bold text-lg text-blue-700 mb-3 flex items-center">
                          <v-icon color="blue" class="mr-2">mdi-lightbulb-on-outline</v-icon> Opportunities
                        </h4>
                        <ul v-if="aiAnalysis.audit.recommendations && aiAnalysis.audit.recommendations.length" class="list-disc list-inside space-y-1 pl-5 text-gray-700">
                          <li v-for="(item, index) in aiAnalysis.audit.recommendations" :key="`swot-opportunity-${index}`">{{ item }}</li>
                        </ul>
                        <p v-else class="text-gray-500 itaic">No specific opportunities identified.</p>
                      </div>
                      <!-- Threats -->
                      <div class="bg-yellow-50 p-4 rounded-lg shadow">
                        <h4 class="font-bold text-lg text-yellow-700 mb-3 flex items-center">
                          <v-icon color="amber" class="mr-2">mdi-alert-outline</v-icon> Threats
                        </h4>
                        <ul v-if="aiAnalysis.audit.threats && aiAnalysis.audit.threats.length" class="list-disc list-inside space-y-1 pl-5 text-gray-700">
                          <li v-for="(item, index) in aiAnalysis.audit.threats" :key="`swot-threat-${index}`">{{ item }}</li>
                        </ul>
                        <p v-else class="text-gray-500 itaic">No specific threats identified.</p>
                      </div>
                    </div>
                  </div>

                  <!-- Category Performance & Detailed Metrics -->
                  <div class="col-span-12 bg-white rounded-lg shadow-md p-4 md:p-6 overflow-hidden">
                    <h3 class="text-lg font-semibold mb-4 md:mb-6">Category Performance & Detailed Metrics</h3>
                    
                    <div class="overflow-x-auto">
                      <div class="min-w-[800px]">
                        <table class="w-full table-auto">
                          <thead class="bg-gray-100">
                            <tr>
                              <th class="p-4 text-left w-1/4 font-bold text-gray-700">Metric</th>
                              <th class="p-4 text-left font-bold text-gray-700">{{ profile1?.title || 'Your Business' }}</th>
                              <th class="p-4 text-left font-bold text-gray-700">{{ profile2?.title || 'Competitor' }}</th>
                              <th class="p-4 text-left w-[100px] font-bold text-gray-700">Difference</th>
                            </tr>
                          </thead>
                          <tbody>
                            <template v-for="(categoryData, categoryKey) in categoryScores">
                              <!-- Category Header Row -->
                              <tr :key="categoryKey" class="border-t-2 border-gray-200 bg-blue-50" :id="`category-detail-${categoryKey}`">
                                <td class="p-4">
                                  <div class="flex items-center gap-2">
                                    <v-icon v-if="categoryKey === 'media'" color="primary">mdi-image-multiple</v-icon>
                                    <div>
                                      <div class="font-bold text-primary text-lg">{{ formatCategoryName(categoryKey) }}</div>
                                      <div class="text-sm text-gray-600 mt-1">{{ categoryDefinitions[categoryKey] || '' }}</div>
                                    </div>
                                  </div>
                                </td>
                                <td class="p-4" :class="getMetricScoreClass(categoryData, 'profile1')">
                                  <div class="flex items-center justify-between">
                                    <span class="font-semibold text-lg">SEO Score: {{ categoryData.profile1?.score?.toFixed(2) || '0.00' }}</span>
                                  </div>
                                </td>
                                <td class="p-4" :class="getMetricScoreClass(categoryData, 'profile2')">
                                  <div class="flex items-center justify-between">
                                    <span class="font-semibold text-lg">SEO Score: {{ categoryData.profile2?.score?.toFixed(2) || '0.00' }}</span>
                                  </div>
                                </td>
                                <td class="p-4 text-lg" :class="categoryData.difference > 0 ? 'text-green-600 font-semibold' : categoryData.difference < 0 ? 'text-orange-600' : 'text-gray-600'">
                                  {{ categoryData.difference !== undefined ? (categoryData.difference > 0 ? '+' : '') + categoryData.difference.toFixed(2) : 'N/A' }}
                                </td>
                              </tr>
                              
                              <!-- Data Points for this Category -->
                              <template v-for="(data, key) in getDataPointsByCategory(categoryKey)">
                                <tr :key="`${categoryKey}-${key}`" class="border-b hover:bg-gray-50">
                                  <td class="pl-8 py-3">
                                    <div class="font-medium text-gray-600">{{ data?.name || formatCategoryName(key) }}</div>
                                    <div class="text-xs text-gray-500 mt-1">{{ data?.criteria || '' }}</div>
                                  </td>
                                  <td class="p-3" :class="getMetricScoreClass(data, 'profile1')">
                                    <div v-if="renderDataPoint(data, key)" class="flex flex-col">
                                      <div class="flex items-start justify-between">
                                        <div class="relative group flex-grow">
                                          <div class="flex items-start gap-2">
                                            <component 
                                              v-if="getCustomComponent(key)"
                                              :is="getCustomComponent(key)"
                                              v-bind="getComponentProps(key, renderDataPoint(data, key).profile1.fullValue, 'profile1', data)"
                                            />
                                            <template v-else-if="isComplexData(renderDataPoint(data, key).profile1.fullValue)">
                                              <RichListDisplay v-if="Array.isArray(renderDataPoint(data, key).profile1.fullValue)" :items="renderDataPoint(data, key).profile1.fullValue" />
                                              <RichObjectDisplay v-else :data="renderDataPoint(data, key).profile1.fullValue" />
                                            </template>
                                            <pre v-else class="whitespace-pre-wrap break-words text-sm flex-grow text-left" v-tooltip="renderDataPoint(data, key).profile1.preview">
                                              {{ renderDataPoint(data, key).profile1.preview }}
                                            </pre>
                                            <button 
                                              v-if="renderDataPoint(data, key).hasFullView && !getCustomComponent(key) && !isComplexData(renderDataPoint(data, key).profile1.fullValue)"
                                              @click="showComparisonView(data, key)"
                                              class="text-xs text-primary hover:text-primary-dark underline mt-1">
                                              View Value
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      <div class="mt-2 text-sm text-gray-600">
                                        Score: {{ data.profile1?.score?.toFixed(2) || '0.00' }}
                                      </div>
                                      <div v-if="data?.profile1?.feedback" class="text-xs text-gray-500 mt-1">
                                        {{ data.profile1.feedback }}
                                      </div>
                                    </div>
                                  </td>
                                  <td class="p-3" :class="getMetricScoreClass(data, 'profile2')">
                                    <div v-if="renderDataPoint(data, key)" class="flex flex-col">
                                      <div class="flex items-start justify-between">
                                        <div class="relative group flex-grow">
                                          <div class="flex items-start gap-2">
                                            <component 
                                              v-if="getCustomComponent(key)"
                                              :is="getCustomComponent(key)"
                                              v-bind="getComponentProps(key, renderDataPoint(data, key).profile2.fullValue, 'profile2', data)"
                                            />
                                            <template v-else-if="isComplexData(renderDataPoint(data, key).profile2.fullValue)">
                                              <RichListDisplay v-if="Array.isArray(renderDataPoint(data, key).profile2.fullValue)" :items="renderDataPoint(data, key).profile2.fullValue" />
                                              <RichObjectDisplay v-else :data="renderDataPoint(data, key).profile2.fullValue" />
                                            </template>
                                            <pre v-else class="whitespace-pre-wrap break-words text-sm flex-grow text-left" v-tooltip="renderDataPoint(data, key).profile2.preview">
                                              {{ renderDataPoint(data, key).profile2.preview }}
                                            </pre>
                                            <button 
                                              v-if="renderDataPoint(data, key).hasFullView && !getCustomComponent(key) && !isComplexData(renderDataPoint(data, key).profile2.fullValue)"
                                              @click="showComparisonView(data, key)"
                                              class="text-xs text-primary hover:text-primary-dark underline mt-1">
                                              View Value
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      <div class="mt-2 text-sm text-gray-600">
                                        Score: {{ data.profile2?.score?.toFixed(2) || '0.00' }}
                                      </div>
                                      <div v-if="data?.profile2?.feedback" class="text-xs text-gray-500 mt-1">
                                        {{ data.profile2.feedback }}
                                      </div>
                                    </div>
                                  </td>
                                  <td class="p-3" :class="data.difference > 0 ? 'text-green-600 font-semibold' : data.difference < 0 ? 'text-orange-600' : 'text-gray-600'">
                                    {{ data.difference !== undefined ? (data.difference > 0 ? '+' : '') + Math.round(data.difference * 10) / 10 : 'N/A' }}
                                  </td>
                                </tr>
                              </template>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <!-- Key Comparisons -->
                  <div v-if="processedKeyDifferences.length > 0" class="col-span-12 bg-white rounded-lg shadow-md p-4 md:p-6">
                    <h3 class="text-lg font-semibold mb-4">Key Comparisons</h3>
                    <v-expansion-panels multiple v-model="expandedPanels">
                      <v-expansion-panel v-for="(diff, index) in processedKeyDifferences" :key="index">
                        <v-expansion-panel-header>
                          <div class="flex items-center justify-between w-full">
                            <span class="font-medium">{{ diff?.area || 'Area' }}</span>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <div class="text-sm font-medium text-gray-500 mb-1">{{ profile1?.title || 'Your Business' }}</div>
                              <div class="bg-gray-50 p-3 rounded break-words" :class="{'text-green-600 font-medium': diff.advantage === 'Profile 1' || diff.advantage === 'profile1'}">
                                {{ diff?.profile1_status || 'No data' }}
                              </div>
                            </div>
                            <div>
                              <div class="text-sm font-medium text-gray-500 mb-1">{{ profile2?.title || 'Competitor' }}</div>
                              <div class="bg-gray-50 p-3 rounded break-words" :class="{'text-green-600 font-medium': diff.advantage === 'Profile 2' || diff.advantage === 'profile2'}">
                                {{ diff?.profile2_status || 'No data' }}
                              </div>
                            </div>
                          </div>
                          <div class="mt-4">
                            <div class="text-sm font-medium text-gray-500 mb-1">Impact Assessment</div>
                            <div class="text-sm break-words">{{ diff?.impact }}</div>
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </div>
                </template> <!-- End of v-if headToHead mode -->
              </div>
            </div>
          </v-tab-item>
          <v-tab-item>
            <div class="w-full">
              <div class="w-full max-w-full p-4 md:p-6 bg-white rounded-lg shadow-md mt-6 mx-auto">
                <h2 class="text-xl font-bold mb-4">Analysis History</h2>
                <p class="text-sm text-gray-600 mb-4">View and manage your competitor analysis reports.</p>
                
                <div v-if="loading" class="text-center py-8">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  <p class="mt-4 text-gray-600">Loading history...</p>
                </div>
                
                <div v-else-if="reportHistory.length === 0" class="text-center text-gray-500 py-6">
                  No analysis history found.
                </div>
                
                <div v-else class="grid grid-cols-1 gap-4">
                  <div v-for="report in reportHistory" :key="report.id" 
                      class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div class="flex justify-between items-start mb-2">
                      <div class="flex-grow">
                        <div class="flex items-center gap-2">
                          <h4 class="font-medium text-base text-primary">{{ report.profile1?.name || 'Business' }}</h4>
                          <span class="text-sm text-gray-500">vs</span>
                          <h4 class="font-medium text-base text-orange-600">{{ report.profile2?.name || 'Competitor' }}</h4>
                        </div>
                        <p class="text-xs text-gray-500 mt-2">Generated: {{ formatDate(report.timestamp) }}</p>
                        <div class="mt-2 text-xs text-gray-600">
                          <div v-if="report.metadata?.generated_at" class="flex items-center gap-1">
                            <v-icon small color="gray">mdi-clock-outline</v-icon>
                            Report Time: {{ formatDate(report.metadata.generated_at) }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="flex justify-end space-x-2 mt-4">
                      <v-btn small outlined color="primary" @click="viewHistoricalReport(report)">
                        <v-icon left small>mdi-eye</v-icon>
                        View
                      </v-btn>
                      <v-btn small color="secondary" @click="rerunFromHistory(report)">
                        <v-icon left small>mdi-refresh</v-icon>
                        Re-run
                      </v-btn>
                      <v-btn small icon color="gray" @click="deleteFromHistory(report.id)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </section>
    </div>
    
    <!-- Raw Data Dialog -->
    <v-dialog v-model="showRawDialog" max-width="800">
      <v-card>
        <v-card-title class="headline">{{ rawDataTitle }}</v-card-title>
        <v-card-text>
          <pre class="bg-gray-100 p-4 rounded overflow-auto max-h-[500px]">{{ rawDataContent }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showRawDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add the comparison dialog -->
    <v-dialog v-model="showComparisonDialog" max-width="1200">
      <v-card>
        <v-card-title class="headline pb-2">{{ comparisonDialogTitle }}</v-card-title>
        <v-card-text>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div class="font-medium text-lg text-primary mb-2">{{ profile1?.title || 'Your Business' }}</div>
              <pre class="bg-gray-100 p-4 rounded overflow-auto max-h-[600px] text-sm whitespace-pre-wrap">{{ formatValueForDisplay(comparisonDialogData?.profile1?.fullValue) }}</pre>
            </div>
            <div>
              <div class="font-medium text-lg text-orange-600 mb-2">{{ profile2?.title || 'Competitor' }}</div>
              <pre class="bg-gray-100 p-4 rounded overflow-auto max-h-[600px] text-sm whitespace-pre-wrap">{{ formatValueForDisplay(comparisonDialogData?.profile2?.fullValue) }}</pre>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showComparisonDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { collection, getDocs, query, where, addDoc, doc, deleteDoc, orderBy, limit } from 'firebase/firestore';
import { colUsers, db } from '@/utils/firebase.utils';
import html2pdf from 'html2pdf.js';
import { auth } from '@/utils/firebase.utils';
import NoLocationsConnected from "@/components/ui/common/NoLocationsConnected.vue";
import connectedAppsMixins from "@/mixins/connected.apps.mixins";
import RichListDisplay from "@/components/RichListDisplay.vue";
import RichObjectDisplay from "@/components/RichObjectDisplay.vue";
import ChipListDisplay from "@/components/datapoints/ChipListDisplay.vue";
import ImagePreviewDisplay from "@/components/datapoints/ImagePreviewDisplay.vue";
import BusinessHoursDisplay from "@/components/datapoints/BusinessHoursDisplay.vue";
import ReviewStarsDisplay from "@/components/datapoints/ReviewStarsDisplay.vue";
import ReviewKeywordsDisplay from "@/components/datapoints/ReviewKeywordsDisplay.vue";
import UpdatesDisplay from "@/components/datapoints/UpdatesDisplay.vue";
import AttributesDisplay from "@/components/datapoints/AttributesDisplay.vue";
import NapConsistencyDisplay from "@/components/datapoints/NapConsistencyDisplay.vue";
import PopularityDisplay from "@/components/datapoints/PopularityDisplay.vue";
import ImageCategoriesDisplay from "@/components/datapoints/ImageCategoriesDisplay.vue";

export default {
  name: 'GmbCompetitorAnalysisReport',
  components: {
    NoLocationsConnected,
    RichListDisplay,
    RichObjectDisplay,
    ChipListDisplay,
    ImagePreviewDisplay,
    BusinessHoursDisplay,
    ReviewStarsDisplay,
    ReviewKeywordsDisplay,
    UpdatesDisplay,
    AttributesDisplay,
    NapConsistencyDisplay,
    PopularityDisplay,
    ImageCategoriesDisplay,
  },
  mixins: [connectedAppsMixins],
  data() {
    return {
      activeTab: 0,
      comparisonMode: 'headToHead',
      locationSearch: "",
      selectedStore: null,
      stores: [],
      selectedWID: null,
      showDropdown: false,
      loading: true,
      selectedCompetitor: null,
      isLoading: false,
      comparisonData: null,
      workspaceChangeListener: null,
      renderErrors: {},
      reportHistory: [],
      activeHistoryReport: null,
      showRawDialog: false,
      rawDataContent: null,
      rawDataTitle: '',
      expandedPanels: [],
      showValidation: false,
      showComparisonDialog: false,
      comparisonDialogData: null,
      comparisonDialogTitle: '',
      cacheKey: null,
      marketReportData: null,
      autocomplete: null,
    };
  },
  computed: {
    currentUID() {
      return this.$store.getters.currentUID;
    },
    WID() {
      return this.$store.getters.WID;
    },
    isAuth() {
      return this.$store.getters.isAuth;
    },
    generateButtonDisabled() {
      if (this.comparisonMode === 'headToHead') {
        return !this.selectedStore || !this.selectedCompetitor;
      } else if (this.comparisonMode === 'multiple') {
        return !this.selectedStore;
      }
      return true;
    },
    filteredStores() {
      if (!this.stores) return [];
      if (!this.locationSearch) return this.stores;

      const searchTerm = this.locationSearch.toLowerCase().trim();
      return this.stores.filter(store => {
        const title = (store.gmbProfile?.title || store.name || '').toLowerCase();
        const address = (store.gmbProfile?.address || store.sub_locality || store.address || '').toLowerCase();
        const category = (store.gmbProfile?.category || '').toLowerCase();
        const city = (store.city || '').toLowerCase();

        return title.includes(searchTerm) || 
               address.includes(searchTerm) || 
               category.includes(searchTerm) || 
               city.includes(searchTerm);
      });
    },
    currentReportData() {
      return this.activeHistoryReport || this.comparisonData;
    },
    profile1() {
      const profile = this.currentReportData?.comparison?.profile1;
      if (!profile) return null;

      if (this.currentReportData?.profile_1?.reviewsDistribution) {
        profile.reviewsDistribution = this.currentReportData.profile_1.reviewsDistribution;
      }
      return profile;
    },
    profile2() {
      const profile = this.currentReportData?.comparison?.profile2;
      if (!profile) return null;

      if (this.currentReportData?.profile_2?.reviewsDistribution) {
        profile.reviewsDistribution = this.currentReportData.profile_2.reviewsDistribution;
      }
      return profile;
    },
    datapointScores() {
      if (!this.currentReportData?.comparison?.datapoint_scores) return {};
      
      const scores = { ...this.currentReportData.comparison.datapoint_scores };
      
      Object.keys(scores).forEach(key => {
        if (key.startsWith('visual_')) {
          const newKey = key.replace('visual_', 'media_');
          scores[newKey] = scores[key];
          delete scores[key];
        }
      });
      
      return scores;
    },
    categoryScores() {
      if (!this.currentReportData?.comparison?.category_scores) return {};
      
      const scores = { ...this.currentReportData.comparison.category_scores };
      
      if (!scores.media) {
        const mediaMetrics = Object.entries(this.datapointScores || {})
          .filter(([key]) => key.startsWith('media_') || key.startsWith('visual_'));
        
        if (mediaMetrics.length > 0) {
          const profile1Scores = mediaMetrics
            .map(([, data]) => data.profile1?.score || 0)
            .filter(score => score > 0);
          
          const profile2Scores = mediaMetrics
            .map(([, data]) => data.profile2?.score || 0)
            .filter(score => score > 0);
          
          scores.media = {
            profile1: {
              score: profile1Scores.length ? 
                profile1Scores.reduce((a, b) => a + b, 0) / profile1Scores.length : 0
            },
            profile2: {
              score: profile2Scores.length ? 
                profile2Scores.reduce((a, b) => a + b, 0) / profile2Scores.length : 0
            },
            difference: (profile1Scores.length && profile2Scores.length) ?
              (profile1Scores.reduce((a, b) => a + b, 0) / profile1Scores.length) -
              (profile2Scores.reduce((a, b) => a + b, 0) / profile2Scores.length) : 0
          };
        }
      }
      
      return scores;
    },
    categoryDefinitions() {
      const definitions = this.currentReportData?.comparison?.category_definitions || {};
      return {
        ...definitions,
        media: definitions.media || "Media content, images, and visual representation"
      };
    },
    aiAnalysis() {
      return this.currentReportData?.comparison?.ai_analysis;
    },
    currentReportStatus() {
      let reportTimestamp;
      if (this.activeHistoryReport) {
        reportTimestamp = this.activeHistoryReport?.comparison?.timestamp;
        return `Viewing historical report from: ${reportTimestamp ? this.formatDate(reportTimestamp) : 'N/A'}`;
      }
      if (this.comparisonData) {
        reportTimestamp = this.comparisonData?.comparison?.timestamp;
        return `Current report generated at: ${reportTimestamp ? this.formatDate(reportTimestamp) : 'N/A'}`;
      }
      return 'No report active.';
    },
    filteredDatapointScores() {
      if (!this.datapointScores) return {};
      const scores = { ...this.datapointScores };
      delete scores.popularity_people_also_search;
      return scores;
    },
    processedKeyDifferences() {
      if (!this.aiAnalysis?.key_differences) return [];
      const p1Name = this.profile1?.title || 'Your Business';
      const p2Name = this.profile2?.title || 'Competitor';
      return this.aiAnalysis.key_differences.map(diff => {
        let impactText = diff.impact || 'No impact data';
        let advantageText = diff.advantage || 'None';

        impactText = impactText.replace(/Profile 1/gi, p1Name).replace(/profile1/gi, p1Name);
        impactText = impactText.replace(/Profile 2/gi, p2Name).replace(/profile2/gi, p2Name);
        
        advantageText = advantageText.replace(/Profile 1/gi, p1Name).replace(/profile1/gi, p1Name);
        advantageText = advantageText.replace(/Profile 2/gi, p2Name).replace(/profile2/gi, p2Name);

        return {
          ...diff,
          impact: impactText,
          processedAdvantageText: advantageText 
        };
      });
    },
    categoryDataPoints() {
      if (!this.datapointScores || !this.categoryScores) return {};
      
      const categoryMappings = {
        'basic_info_name': 'basic_info',
        'basic_info_category': 'basic_info',
        'basic_info_additional_categories': 'basic_info',
        'basic_info_address': 'basic_info',
        'basic_info_phone': 'basic_info',
        'basic_info_website': 'basic_info',
        'basic_info_hours': 'basic_info',
        'reviews_count': 'reviews',
        'reviews_average_rating': 'reviews',
        'reviews_distribution': 'reviews',
        'reviews_one_star': 'reviews',
        'reviews_two_star': 'reviews',
        'reviews_three_star': 'reviews',
        'reviews_four_star': 'reviews',
        'reviews_five_star': 'reviews',
        'reviews_keywords_total': 'reviews',
        'reviews_keywords_seo': 'reviews',
        'reviews_tags': 'reviews',
        'media_images_count': 'media',
        'media_image_categories': 'media',
        'media_profile_image': 'media',
        'media_image_urls': 'media',
        'media_video_count': 'media',
        'media_content_quality': 'media',
        'visual_images_count': 'media',
        'visual_image_categories': 'media',
        'visual_profile_image': 'media',
        'visual_image_urls': 'media',
        'engagement_qna': 'engagement',
        'engagement_updates': 'engagement',
        'engagement_owner_responses': 'engagement',
        'engagement_customer_updates_count': 'engagement',
        'engagement_owner_updates_count': 'engagement',
        'engagement_customer_updates_recency': 'engagement',
        'engagement_owner_updates_quality': 'engagement',
        'engagement_owner_updates_recency': 'engagement',
        'attributes_service_options': 'business_attributes',
        'attributes_amenities': 'business_attributes',
        'attributes_payment_options': 'business_attributes',
        'attributes_accessibility': 'business_attributes',
        'attributes_business_description': 'business_attributes',
        'local_seo_attributes': 'local_seo',
        'local_seo_nap_consistency': 'local_seo',
        'local_seo_keywords': 'local_seo',
        'popularity_live_data': 'popularity',
        'popularity_popular_times': 'popularity',
        'popularity_peak_hours': 'popularity',
        'popularity_busiest_days': 'popularity',
        'popularity_weekend_traffic': 'popularity',
        'popularity_weekday_traffic': 'popularity',
        'popularity_people_also_search': 'popularity',
      };
      
      const categoryMap = {};
      Object.entries(this.datapointScores).forEach(([key, data]) => {
        const category = categoryMappings[key] || key.split('_')[0];
        
        if (this.categoryScores[category]) {
          if (!categoryMap[category]) categoryMap[category] = {};
          categoryMap[category][key] = {
            ...data,
            name: data.name || this.formatMetricName(key),
            criteria: data.criteria || this.getDefaultCriteria(key)
          };
        }
      });
      
      return categoryMap;
    },
    formatMetricName() {
      return function(key) {
        const specialCases = {
          'local_seo': 'Local SEO',
          'media': 'Media & Content'
        };
        
        if (specialCases[key]) return specialCases[key];
        return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
    },
    getDefaultCriteria() {
      return function(key) {
        const criteriaMap = {
          'reviews_count': 'Higher number of authentic reviews indicates better engagement',
          'visual_images_count': 'More high-quality images showcase the business better',
          'popularity_peak_hours': 'Clear pattern of peak hours indicates consistent traffic',
          'local_seo_keywords': 'Strategic use of relevant keywords improves visibility',
          'media_images_count': 'Higher number of high-quality images improves visual appeal',
          'media_image_categories': 'Diverse image categories covering all aspects of business',
          'media_profile_image': 'Professional and appealing profile image',
          'media_video_count': 'Video content enhances engagement',
          'media_content_quality': 'Overall quality of visual content',
        };
        return criteriaMap[key] || '';
      }
    },
  },
  methods: {
    toggleStoreDropdown(event) {
      this.showDropdown = !this.showDropdown;
    },
    getCustomComponent(dataPointKey) {
      const keyMap = {
        'basic_info_hours': 'BusinessHoursDisplay',
        'reviews_one_star': 'ReviewStarsDisplay',
        'reviews_two_star': 'ReviewStarsDisplay',
        'reviews_three_star': 'ReviewStarsDisplay',
        'reviews_four_star': 'ReviewStarsDisplay',
        'reviews_five_star': 'ReviewStarsDisplay',
        'reviews_keywords_seo': 'ReviewKeywordsDisplay',
        'reviews_keywords_total': 'ReviewKeywordsDisplay',
        'engagement_owner_updates': 'UpdatesDisplay',
        'engagement_customer_updates': 'UpdatesDisplay',
        'local_seo_attributes': 'AttributesDisplay',
        'attributes_service_options': 'AttributesDisplay',
        'attributes_amenities': 'AttributesDisplay',
        'attributes_payment_options': 'AttributesDisplay',
        'attributes_accessibility': 'AttributesDisplay',
        'local_seo_nap_consistency': 'NapConsistencyDisplay',
        'popularity_popular_times': 'PopularityDisplay',
        'popularity_peak_hours': 'PopularityDisplay',
        'popularity_busiest_days': 'PopularityDisplay',
        'popularity_weekend_traffic': 'PopularityDisplay',
        'popularity_weekday_traffic': 'PopularityDisplay',
        'media_profile_image': 'ImagePreviewDisplay',
        'media_image_categories': 'ImageCategoriesDisplay',
      };
      if (keyMap[dataPointKey]) {
        return keyMap[dataPointKey];
      }
      return null; 
    },
    getComponentProps(dataPointKey, value, profileKey, fullDataEntry) {
      const commonProps = { emptyText: 'N/A' };
      switch (dataPointKey) {
        case 'basic_info_hours': 
          return { ...commonProps, hoursData: value, title: '' };
        case 'reviews_one_star':
          return { ...commonProps, starRating: 1, count: (typeof value === 'object' && value !== null ? value.count : value) || 0, label: '1 Star Reviews' };
        case 'reviews_two_star':
          return { ...commonProps, starRating: 2, count: (typeof value === 'object' && value !== null ? value.count : value) || 0, label: '2 Star Reviews' };
        case 'reviews_three_star':
          return { ...commonProps, starRating: 3, count: (typeof value === 'object' && value !== null ? value.count : value) || 0, label: '3 Star Reviews' };
        case 'reviews_four_star':
          return { ...commonProps, starRating: 4, count: (typeof value === 'object' && value !== null ? value.count : value) || 0, label: '4 Star Reviews' };
        case 'reviews_five_star':
          return { ...commonProps, starRating: 5, count: (typeof value === 'object' && value !== null ? value.count : value) || 0, label: '5 Star Reviews' };
        case 'reviews_keywords_seo':
          return { ...commonProps, keywords: value, title: 'SEO Keywords Value', valueType: 'seo_value' };
        case 'reviews_keywords_total':
          return { ...commonProps, keywords: value, title: 'Keywords Count', valueType: 'count' };
        case 'engagement_owner_updates':
        case 'engagement_customer_updates':
          return { ...commonProps, updates: value, title: '' };
        case 'local_seo_attributes':
        case 'attributes_service_options':
        case 'attributes_amenities':
        case 'attributes_payment_options':
        case 'attributes_accessibility':
          return { ...commonProps, attributes: value, title: '' };
        case 'local_seo_nap_consistency':
          return { ...commonProps, consistencyData: value, title: '' };
        case 'popularity_popular_times': 
        case 'popularity_peak_hours': 
        case 'popularity_busiest_days':
        case 'popularity_weekend_traffic':
        case 'popularity_weekday_traffic':
          return { ...commonProps, popularityData: { [dataPointKey.replace('popularity_', '')]: value }, title: '' };
        case 'media_profile_image':
          return { ...commonProps, imageUrl: value, altText: `${fullDataEntry?.name || dataPointKey} - ${profileKey}` };
        case 'media_image_categories':
          return { ...commonProps, categories: value, title: '' };
        default:
          if (Array.isArray(value) && value.every(item => typeof item === 'string')) {
            return { ...commonProps, items: value, title: '' };
          }
          return { ...commonProps, data: value };
      }
    },
    isComplexData(value) {
      return typeof value === 'object' && value !== null;
    },
    async fetchStores() {
      if (!this.isAuth) {
        console.warn("Cannot fetch stores: user not authenticated", { isAuth: this.isAuth });
        this.loading = false;
        return;
      }
      
      if (!this.currentUID || !this.WID) {
        console.warn("Cannot fetch stores: missing currentUID or WID", { 
          isAuth: this.isAuth,
          currentUID: this.currentUID, 
          WID: this.WID 
        });
        this.loading = false;
        return;
      }

      this.loading = true;
      try {
        const storeRef = collection(db, "users", this.currentUID, "fly_workspaces");
        const storeQuery = query(
          storeRef,
          where("admin", "==", this.WID),
          where("storeWorkspace", "==", true)
        );
        const querySnapshot = await getDocs(storeQuery);
        this.stores = querySnapshot.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          const placeId = data.gmbProfile?.place_id || 
                        data.place_id || 
                        data.gmb_shallow_profile_data?.place_id || 
                        data.gmb_profile_data?.place_id ||
                        data.gmbProfile?.placeId ||
                        null;
          
          if (!placeId) {
            console.warn("Place ID not found for store:", data.gmbProfile?.title || data.name, data);
          }
          
          return {
            ...data,
            wid: docSnapshot.id,
            placeId: placeId 
          };
        });
      } catch (error) {
        console.error("Error fetching stores:", error);
        this.$snackbar.show({text: "Error fetching your locations.", color: "error"});
      } finally {
        this.loading = false;
      }
    },
    async resetOnWorkspaceChange() {
      this.selectedStore = null;
      this.selectedWID = null;
      this.selectedCompetitor = null;
      this.comparisonData = null;
      this.activeHistoryReport = null;
      this.stores = [];
      this.renderErrors = {};
      this.locationSearch = '';
      this.activeTab = 0;
      if (this.$refs.competitorAutocomplete) {
        this.$refs.competitorAutocomplete.value = '';
      }
      if (this.autocomplete) this.autocomplete = null;
      
      await Promise.all([
        this.fetchStores(),
        this.loadHistoryFromFirestore()
      ]);
    },
    openDropdown() {
      this.showDropdown = true;
      if (this.selectedStore) {
        this.locationSearch = '';
      }
    },
    handleSearchInput() {
      this.showDropdown = true;
    },
    selectStore(store) {
      this.selectedStore = { ...store };
      this.showDropdown = false;
      this.selectedWID = store.wid;
      this.locationSearch = store.gmbProfile?.title || store.name || '';
      this.selectedCompetitor = null;
      this.clearCache();
      
      if (this.$refs.competitorAutocomplete) {
        this.$refs.competitorAutocomplete.value = '';
      }
      
      if (!this.autocomplete && window.google && window.google.maps && window.google.maps.places) {
        this.initAutocomplete();
      } else if (this.autocomplete) {
        this.initAutocomplete();
      }
    },
    handleConnectGMB() {
      try {
        this.$snackbar.show("Please wait a moment");
        this.isLoading = true;
        this.initConnection("google_business_profile", "bulk");
        setTimeout(() => { this.$snackbar.hide(); }, 2000);
      } finally {
        this.isLoading = false;
      }
    },
    initAutocomplete() {
      if (!this.$refs.competitorAutocomplete) {
        console.warn("Competitor autocomplete input not found");
        return;
      }
      
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.warn("Google Maps Places API not loaded, retrying in 1 second...");
        setTimeout(() => {
          this.initAutocomplete();
        }, 1000);
        return;
      }
      
      if (this.autocomplete) {
        // Clear existing listeners
        if (window.google.maps.event) {
          window.google.maps.event.clearInstanceListeners(this.autocomplete);
        }
        this.autocomplete = null;
      }

      const input = this.$refs.competitorAutocomplete;

      try {
        // Use the traditional Autocomplete API instead of the new PlaceAutocompleteElement
        this.autocomplete = new window.google.maps.places.Autocomplete(input, {
          types: ['establishment'],
          fields: ['place_id', 'name', 'formatted_address', 'geometry']
        });

        // Location biasing
        if (this.selectedStore) {
          const storeLocation = this.selectedStore.gmbProfile?.location || 
                               this.selectedStore.location ||
                               (this.selectedStore.lat && this.selectedStore.lng ? 
                                 { lat: this.selectedStore.lat, lng: this.selectedStore.lng } : null);
          
          if (storeLocation && storeLocation.lat && storeLocation.lng) {
            const circle = new window.google.maps.Circle({
              center: { lat: storeLocation.lat, lng: storeLocation.lng },
              radius: 15000 // 15km radius
            });
            this.autocomplete.setBounds(circle.getBounds());
          }
        }

        // Add event listener for place selection
        this.autocomplete.addListener('place_changed', () => {
          const place = this.autocomplete.getPlace();
          
          if (!place || !place.place_id) {
            console.warn("Invalid place selected");
            return;
          }

          this.selectedCompetitor = {
            placeId: place.place_id,
            name: place.name,
            address: place.formatted_address,
            location: place.geometry ? {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            } : null
          };

          this.$snackbar.show({
            text: `Selected competitor: ${place.name}`,
            color: "success"
          });

          // Clear the input after selection
          setTimeout(() => {
            if (this.$refs.competitorAutocomplete) {
              this.$refs.competitorAutocomplete.value = '';
            }
          }, 100);
        });

        console.log("Google Places Autocomplete initialized successfully");
      } catch (error) {
        console.error("Error creating Autocomplete:", error);
        this.$snackbar.show({
          text: "Failed to initialize competitor search. Please refresh the page.",
          color: "error"
        });
      }
    },
    async generateReport(isRerun = false) {
      this.showValidation = true;
      this.isLoading = true;
      this.activeHistoryReport = null; // Always clear history view when generating new report

      if (this.comparisonMode === 'headToHead') {
        if (!this.selectedStore || !this.selectedCompetitor) {
          this.$snackbar.show({text: "Please select your location and a competitor for Head to Head comparison.", color: "error"});
          this.isLoading = false; 
          return;
        }
        if (isRerun) this.clearCache(); // Clear cache only for headToHead if re-run

        // Use existing cache logic for headToHead
        if (!isRerun) {
          const cachedData = this.getCachedReport();
          if (cachedData) {
            console.log("Using cached head-to-head report data");
            this.comparisonData = cachedData;
            this.marketReportData = null; // Clear other mode's data
            this.$nextTick(() => {
              if (this.aiAnalysis?.key_differences) {
                this.expandedPanels = [...Array(this.aiAnalysis.key_differences.length).keys()];
              }
            });
            this.isLoading = false;
            return;
          }
        }

        this.comparisonData = null; // Clear previous before new fetch
        this.marketReportData = null; 

        try {
          const storePlaceId = this.selectedStore.placeId;
          if (!storePlaceId) throw new Error('No place ID found for your selected store');
          
          const competitorPlaceId = this.selectedCompetitor.placeId;
          if(!competitorPlaceId) throw new Error('No place ID found for selected competitor');

          const storeQuery = encodeURIComponent(
            `${this.selectedStore.gmbProfile?.title || this.selectedStore.name || ''} ${this.selectedStore.sub_locality || this.selectedStore.address || ''}`
          ).trim();
          const storeUrl = `https://www.google.com/maps/search/?api=1&query=${storeQuery}&query_place_id=${storePlaceId}`;

          const competitorQuery = encodeURIComponent(
            `${this.selectedCompetitor.name || ''} ${this.selectedCompetitor.address || ''}`
          ).trim();
          const competitorUrl = `https://www.google.com/maps/search/?api=1&query=${competitorQuery}&query_place_id=${competitorPlaceId}`;

          const payload = {
            url1: storeUrl,
            url2: competitorUrl
          };
          
          const currentHost = window.location.host;
          const isLocalDevelopment = currentHost === 'localhost:8080' || currentHost === 'localhost:8081';
          const apiEndpoint = isLocalDevelopment 
            ? (process.env.VUE_APP_COMPARE_GBP || 'http://localhost:8888/compare-gbp')
            : (process.env.VUE_APP_COMPARE_GBP || 'https://google-rank-tracker-in-nfp3rxpvjq-el.a.run.app/compare-gbp');

          const response = await fetch(apiEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API request failed: ${response.statusText}. Details: ${errorText}`);
          }
          
          const result = await response.json();
          this.comparisonData = result;
          
          this.setCachedReport(result); // Cache for headToHead
          
          this.$nextTick(() => {
            if (this.aiAnalysis?.key_differences) {
              this.expandedPanels = [...Array(this.aiAnalysis.key_differences.length).keys()];
            }
          });
          
          if (!isRerun) {
            await this.saveToHistory(result); // Save headToHead to history
          }
        } catch (error) {
          console.error("Error generating head-to-head report:", error);
          this.$snackbar.show({ text: error.message || "Failed to generate head-to-head report.", color: "error" });
        }

      } else if (this.comparisonMode === 'multiple') {
        if (!this.selectedStore || !this.selectedStore.placeId) {
          this.$snackbar.show({text: "Please select a valid location for Multiple Competitors comparison.", color: "error"});
          this.isLoading = false;
          return;
        }
        // NOTE: Caching and re-run logic for 'multiple' mode is not implemented in this step.
        // isRerun flag is not currently used for this mode.

        this.marketReportData = null; // Clear previous before new fetch
        this.comparisonData = null; // Clear other mode's data

        try {
          // Expanded lat/lng extraction
          let lat, lng;
          if (this.selectedStore.gmbProfile && this.selectedStore.gmbProfile.latlng && typeof this.selectedStore.gmbProfile.latlng.latitude === 'number' && typeof this.selectedStore.gmbProfile.latlng.longitude === 'number') {
            lat = this.selectedStore.gmbProfile.latlng.latitude;
            lng = this.selectedStore.gmbProfile.latlng.longitude;
          } else if (this.selectedStore.gmbProfile && this.selectedStore.gmbProfile.location && typeof this.selectedStore.gmbProfile.location.lat === 'number' && typeof this.selectedStore.gmbProfile.location.lng === 'number') {
            lat = this.selectedStore.gmbProfile.location.lat;
            lng = this.selectedStore.gmbProfile.location.lng;
          } else if (this.selectedStore.location && typeof this.selectedStore.location.lat === 'number' && typeof this.selectedStore.location.lng === 'number') {
            lat = this.selectedStore.location.lat;
            lng = this.selectedStore.location.lng;
          } else if (typeof this.selectedStore.lat === 'number' && typeof this.selectedStore.lng === 'number') {
            lat = this.selectedStore.lat;
            lng = this.selectedStore.lng;
          }
          
          if (!lat || !lng) {
              console.error("Latitude/Longitude not found directly. Selected store data for 'Multiple Competitors' mode:", JSON.parse(JSON.stringify(this.selectedStore)));
              throw new Error("Latitude and longitude are missing or in an unexpected format for the selected location. Please ensure the location data is complete.");
          }

          // Construct the Google Maps URL for the selected store
          const gmbUrl = `https://www.google.com/maps/place/${encodeURIComponent(this.selectedStore.gmbProfile?.title || this.selectedStore.name)}/@${lat},${lng},15z/data=!4m6!3m5!1s${this.selectedStore.placeId}!8m2!3d${lat}!4d${lng}`;

          const payload = {
            url: gmbUrl,
            radius: "5000", // Default radius, can be made configurable later
            max_competitors: 5, // Default max competitors, can be configurable
            country: this.selectedStore.country_code?.toLowerCase() || "us",
          };

          console.log("Requesting full market report with body:", payload);
          
          const currentHost = window.location.host;
          const isLocalDevelopment = currentHost === 'localhost:8080' || currentHost === 'localhost:8081';
          // IMPORTANT: Use the correct endpoint for whole market comparison
          const apiEndpoint = isLocalDevelopment 
            ? (process.env.VUE_APP_COMPARE_GBP_WHOLE_MARKET || 'http://localhost:8888/compare-gbp-whole-market') 
            : (process.env.VUE_APP_COMPARE_GBP_WHOLE_MARKET || 'https://google-rank-tracker-in-nfp3rxpvjq-el.a.run.app/compare-gbp-whole-market');

          const response = await fetch(apiEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
              // Add auth headers if your endpoint requires them
            },
            body: JSON.stringify(payload)
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: response.statusText }));
            throw new Error(errorData.message || `API request failed with status ${response.status}`);
          }
          
          const result = await response.json();
          console.log("Multiple Competitors API Response:", result);

          if (result && result.source_business) { 
            this.marketReportData = result;
          } else {
            throw new Error("Received invalid data from the multiple competitors API.");
          }
          // History and caching for this mode can be added as a follow-up

        } catch (error) {
          console.error("Error generating multiple competitors report:", error);
          this.$snackbar.show({ text: error.message || "Failed to generate multiple competitors report.", color: "error", timeout: 6000 });
        }
      }

      this.isLoading = false;
      this.showValidation = false; // Hide validation messages after attempting report gen
    },
    resetForm() {
      this.selectedStore = null;
      this.selectedWID = null;
      this.selectedCompetitor = null;
      this.comparisonData = null;
      this.activeHistoryReport = null; 
      this.marketReportData = null; // ADD this
      this.locationSearch = "";
      this.showValidation = false;
      this.clearCache();
      // this.comparisonMode = 'headToHead'; // Optionally reset mode, or keep user's choice
      if (this.$refs.competitorAutocomplete) this.$refs.competitorAutocomplete.value = '';
    },
    resetFormAndSwitchTab() {
        this.resetForm();
        this.activeTab = 0;
    },
    formatCategoryName(key) {
      if (!key) return '';
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    truncateValue(value, maxLines = 3) {
      if (typeof value === 'object') {
        const stringified = JSON.stringify(value, null, 2);
        const lines = stringified.split('\n');
        if (lines.length > maxLines) {
          return lines.slice(0, maxLines).join('\n') + '...';
        }
        return stringified;
      }
      return value;
    },
    renderDataPoint(data, key) {
      if (!data || typeof data !== 'object') {
        return { type: 'simple', value: data || 'N/A', score: undefined, error: !data };
      }
      
      const profile1Value = data.profile1?.value;
      const profile2Value = data.profile2?.value;
      const profile1Score = data.profile1?.score;
      const profile2Score = data.profile2?.score;
      
      const needsFullView = (value) => {
        if (!value) return false;
        if (Array.isArray(value) && value.length > 3) return true;
        if (typeof value === 'object') {
          const stringified = JSON.stringify(value, null, 2);
          return stringified.split('\n').length > 3;
        }
        return false;
      };
      
      const hasFullView = needsFullView(profile1Value) || needsFullView(profile2Value);
      
      return {
        type: typeof profile1Value === 'object' ? 'complex' : 'simple',
        profile1: {
          preview: this.getPreviewText(profile1Value),
          fullValue: profile1Value,
          score: profile1Score,
          feedback: data.profile1?.feedback
        },
        profile2: {
          preview: this.getPreviewText(profile2Value),
          fullValue: profile2Value,
          score: profile2Score,
          feedback: data.profile2?.feedback
        },
        hasFullView
      };
    },
    showRawData(content, title) {
      this.rawDataContent = JSON.stringify(content, null, 2);
      this.rawDataTitle = title;
      this.showRawDialog = true;
    },
    downloadReport() {
      if (!this.currentReportData) {
        this.$snackbar.show({ text: "No report data to download.", color: "warning" });
        return;
      }
      const element = this.$el.querySelector('.report-content'); 
      if (element) {
        const p1Title = this.profile1?.title || 'Profile1';
        const p2Title = this.profile2?.title || 'Profile2';
        const options = {
          margin: 0.5,
          filename: `Competitor_Analysis_${p1Title}_vs_${p2Title}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true }, 
          jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' }
        };
        html2pdf().from(element).set(options).save();
      } else {
        this.$snackbar.show({ text: "Could not find report content to download.", color: "error" });
      }
    },
    async saveToHistory(reportDataToSave) {
      if (!reportDataToSave || !reportDataToSave.comparison) {
        console.error("Attempted to save invalid report data to history", reportDataToSave);
        return;
      }

      try {
        if (!this.currentUID || !this.WID) {
          console.error("Missing required IDs:", { uid: this.currentUID, wid: this.WID });
          throw new Error("Missing required user or workspace ID");
        }

        console.log("Starting to save report to history...", {
          uid: this.currentUID,
          wid: this.WID,
          hasComparison: !!reportDataToSave.comparison
        });

        const reportEntry = {
          timestamp: new Date().toISOString(),
          profile1: {
            name: reportDataToSave.comparison.profile1?.title || this.selectedStore?.gmbProfile?.title || 'Profile 1',
            placeId: reportDataToSave.comparison.profile1?.placeId || this.selectedStore?.placeId,
            url: reportDataToSave.comparison.profile1?.url || null,
            reviewsCount: reportDataToSave.profile_1?.reviewsCount || 0,
            totalScore: reportDataToSave.profile_1?.totalScore || 0
          },
          profile2: {
            name: reportDataToSave.comparison.profile2?.title || this.selectedCompetitor?.name || 'Profile 2',
            placeId: reportDataToSave.comparison.profile2?.placeId || this.selectedCompetitor?.placeId,
            url: reportDataToSave.comparison.profile2?.url || null,
            reviewsCount: reportDataToSave.profile_2?.reviewsCount || 0,
            totalScore: reportDataToSave.profile_2?.totalScore || 0
          },
          report_cached_at: reportDataToSave.report_cached_at || null,
          metadata: {
            ...(reportDataToSave.metadata || {}),
            generated_at: reportDataToSave.comparison.timestamp || new Date().toISOString(),
            overall_scores: reportDataToSave.comparison.overall_scores || null,
            category_scores: reportDataToSave.comparison.category_scores || null
          },
          comparison: reportDataToSave.comparison,
          profile_1: reportDataToSave.profile_1,
          profile_2: reportDataToSave.profile_2,
          full_report: reportDataToSave
        };

        const historyRef = collection(db, "users", this.currentUID, "fly_workspaces", this.WID, "competition_track_h2h");
        const docRef = await addDoc(historyRef, reportEntry);
        console.log("Report saved successfully with ID:", docRef.id);

        await this.loadHistoryFromFirestore();
        
        this.$snackbar.show({ text: "Report saved to history.", color: "success" });
      } catch (error) {
        console.error("Error saving report to history:", error);
        this.$snackbar.show({ text: "Failed to save report history.", color: "error" });
      }
    },
    async loadHistoryFromFirestore() {
      if (!this.isAuth) {
        console.warn("Cannot load history: user not authenticated", { isAuth: this.isAuth });
        this.loading = false;
        return;
      }
      
      if (!this.currentUID || !this.WID) {
        console.warn("Cannot load history: missing currentUID or WID", { 
          isAuth: this.isAuth,
          currentUID: this.currentUID, 
          WID: this.WID 
        });
        this.loading = false;
        return;
      }

      this.loading = true;
      try {
        console.log("Loading history from Firestore...", {
          uid: this.currentUID,
          wid: this.WID
        });

        const historyRef = collection(db, "users", this.currentUID, "fly_workspaces", this.WID, "competition_track_h2h");
        const q = query(historyRef, orderBy("timestamp", "desc"), limit(50));
        const querySnapshot = await getDocs(q);
        
        this.reportHistory = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            timestamp: data.timestamp,
            metadata: {
              ...data.metadata,
              generated_at: data.metadata?.generated_at || data.timestamp
            }
          };
        });

        console.log("History loaded successfully:", {
          reportsCount: this.reportHistory.length,
          sampleReportId: this.reportHistory[0]?.id || 'no reports',
          hasFullReport: !!this.reportHistory[0]?.full_report
        });
      } catch (error) {
        console.error("Error loading history from Firestore:", error);
        this.$snackbar.show({ text: "Failed to load report history.", color: "error" });
        this.reportHistory = [];
      } finally {
        this.loading = false;
      }
    },
    async deleteFromHistory(reportId) {
      try {
        const historyRef = doc(db, "users", this.currentUID, "fly_workspaces", this.WID, "competition_track_h2h", reportId);
        await deleteDoc(historyRef);
        
        if (this.activeHistoryReport && this.activeHistoryReport.id === reportId) {
          this.clearHistoryView();
        }
        
        await this.loadHistoryFromFirestore();
        this.$snackbar.show({text: "Report deleted from history.", color: "info"});
      } catch (error) {
        console.error("Error deleting report from history:", error);
        this.$snackbar.show({ text: "Failed to delete report.", color: "error" });
      }
    },
    async viewHistoricalReport(report) {
      try {
        console.log("Viewing historical report:", {
          reportId: report.id,
          hasFullReport: !!report.full_report,
          hasComparison: !!report.comparison
        });

        if (!report.full_report && !report.comparison) {
          this.$snackbar.show({ 
            text: "This report's data is not available. Please generate a new report.", 
            color: "warning" 
          });
          return;
        }
        
        const historicalReport = {
          comparison: report.comparison || report.full_report?.comparison,
          profile_1: report.profile_1 || report.full_report?.profile_1,
          profile_2: report.profile_2 || report.full_report?.profile_2,
          ...report.full_report
        };

        this.activeHistoryReport = historicalReport;
        this.activeTab = 0;
        
        this.$nextTick(() => {
          if (this.aiAnalysis?.key_differences) {
            this.expandedPanels = [...Array(this.aiAnalysis.key_differences.length).keys()];
          }
        });
      } catch (error) {
        console.error("Error viewing historical report:", error);
        this.$snackbar.show({ 
          text: "Failed to load the historical report.", 
          color: "error" 
        });
      }
    },
    rerunFromHistory(reportEntry) {
      if (!reportEntry?.profile1?.placeId || !reportEntry?.profile2?.placeId) {
        this.$snackbar.show({text: "Historical report data is incomplete for re-run.", color: "error"});
        return;
      }

      const originalStore = this.stores.find(s => s.placeId === reportEntry.profile1.placeId);
      if (originalStore) {
        this.selectedStore = JSON.parse(JSON.stringify(originalStore));
      } else {
        this.selectedStore = {
          placeId: reportEntry.profile1.placeId,
          gmbProfile: { 
            title: reportEntry.profile1.name, 
            place_id: reportEntry.profile1.placeId 
          },
          name: reportEntry.profile1.name,
          wid: 'historical-' + Date.now()
        };
      }
      
      this.locationSearch = this.selectedStore.gmbProfile?.title || this.selectedStore.name || '';
      this.selectedWID = this.selectedStore.wid;

      this.selectedCompetitor = {
        placeId: reportEntry.profile2.placeId,
        name: reportEntry.profile2.name,
        address: reportEntry.profile2.url || ''
      };

      if (this.$refs.competitorAutocomplete) {
        this.$refs.competitorAutocomplete.value = reportEntry.profile2.name || '';
      }

      this.activeTab = 0;
      this.comparisonData = null;
      this.activeHistoryReport = null;

      this.$nextTick(() => {
        this.generateReport(true);
      });
    },
    clearHistoryView() {
      this.activeHistoryReport = null;
      if (this.comparisonData && this.aiAnalysis?.key_differences) {
         this.expandedPanels = [...Array(this.aiAnalysis.key_differences.length).keys()];
      } else if (!this.comparisonData) {
          this.resetForm(); 
      }
    },
    formatAdvantage(advantage) {
      if (advantage === 'Profile 1' || advantage === 'profile1') {
        return this.profile1?.title || 'Your Business';
      }
      if (advantage === 'Profile 2' || advantage === 'profile2') {
        return this.profile2?.title || 'Competitor';
      }
      return advantage || 'None';
    },
    getAdvantageClass(advantage) {
      if (advantage === 'Profile 1' || advantage === 'profile1') return 'text-green-600';
      if (advantage === 'Profile 2' || advantage === 'profile2') return 'text-red-600';
      return 'text-gray-600';
    },
    getMetricScoreClass(metricData, currentProfileKey) {
        if (!metricData || metricData.difference === undefined || metricData.difference === null || metricData.difference === 0) {
            return ''; 
        }
        const isProfile1 = currentProfileKey === 'profile1';
        const difference = parseFloat(metricData.difference);

        if (isProfile1) {
            if (difference > 0) return 'text-green-600 font-semibold';
            if (difference < 0) return 'text-orange-600';
        } else {
            if (difference < 0) return 'text-green-600 font-semibold';
            if (difference > 0) return 'text-orange-600';
        }
        return '';
    },
    getDataPointsByCategory(categoryKey) {
      return this.categoryDataPoints[categoryKey] || {};
    },
    formatDate(timestamp) {
      // Ensure this handles Firebase Timestamps if they come from marketReportData.metadata.generated_at
      if (!timestamp) return 'N/A';
      let dateToFormat;
      if (timestamp.seconds) { // Check for Firebase Timestamp object
        dateToFormat = new Date(timestamp.seconds * 1000);
      } else {
        dateToFormat = new Date(timestamp);
      }
      const day = dateToFormat.getDate().toString().padStart(2, '0');
      const month = dateToFormat.toLocaleString('default', { month: 'short' });
      const year = dateToFormat.getFullYear();
      const time = dateToFormat.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' });
      return `${day} ${month} ${year} ${time}`;    
    },
    formatValueForDisplay(value) {
      if (!value) return 'N/A';
      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          return value.map(item => {
            if (typeof item === 'object') {
              return JSON.stringify(item, null, 2);
            }
            return item;
          }).join('\n');
        }
        return JSON.stringify(value, null, 2);
      }
      return value.toString();
    },
    getPreviewText(value, maxLines = 6) {
      if (value === null || value === undefined) return 'N/A';
      
      let stringValue;
      if (typeof value === 'object') {
        stringValue = JSON.stringify(value, null, 2);
      } else {
        stringValue = String(value);
      }
      
      const lines = stringValue.split('\n');
      if (lines.length > maxLines) {
        return lines.slice(0, maxLines).join('\n') + '\n...'; // Simplified ellipsis
      }
      return stringValue;
    },
    showComparisonView(data, key) {
      const renderedData = this.renderDataPoint(data, key);
      this.comparisonDialogData = renderedData;
      this.comparisonDialogTitle = data.name || this.formatMetricName(key);
      this.showComparisonDialog = true;
    },
    getCacheKey() {
      if (!this.selectedStore?.placeId || !this.selectedCompetitor?.placeId) return null;
      return `competitor_analysis_${this.selectedStore.placeId}_${this.selectedCompetitor.placeId}`;
    },
    getCachedReport() {
      const key = this.getCacheKey();
      if (!key) return null;

      try {
        const cached = localStorage.getItem(key);
        if (!cached) return null;

        const { data, timestamp } = JSON.parse(cached);
        
        const now = new Date().getTime();
        const cacheAge = now - timestamp;
        const cacheValidityPeriod = 24 * 60 * 60 * 1000; 

        if (cacheAge > cacheValidityPeriod) {
          localStorage.removeItem(key);
          return null;
        }

        console.log("Retrieved report from cache:", { key, timestamp: new Date(timestamp) });
        return data;
      } catch (error) {
        console.error("Error retrieving cached report:", error);
        return null;
      }
    },
    setCachedReport(data) {
      const key = this.getCacheKey();
      if (!key || !data) return;

      try {
        const cacheData = {
          data,
          timestamp: new Date().getTime()
        };
        localStorage.setItem(key, JSON.stringify(cacheData));
        console.log("Cached report data:", { key, timestamp: new Date(cacheData.timestamp) });
      } catch (error) {
        console.error("Error caching report:", error);
      }
    },
    clearCache() {
      const key = this.getCacheKey();
      if (key) {
        localStorage.removeItem(key);
        console.log("Cleared cache for key:", key);
      }
    },
    clearSelectedStore(event) {
      if (event) {
        event.stopPropagation();
      }
      this.selectedStore = null;
      this.selectedWID = null;
      this.locationSearch = '';
      this.selectedCompetitor = null;
      this.showDropdown = false;
      
      if (this.$refs.competitorAutocomplete) {
        this.$refs.competitorAutocomplete.value = '';
      }
      this.clearCache();
    },
    handleModeChange() {
      // Reset relevant data when mode changes to avoid confusion
      this.comparisonData = null;
      this.marketReportData = null;
      this.selectedCompetitor = null; // Clear competitor if switching away from headToHead
      if (this.$refs.competitorAutocomplete) {
        this.$refs.competitorAutocomplete.value = '';
      }
      this.activeHistoryReport = null; // Clear any active history view
      // Caching might need more specific handling here later if implemented for 'multiple' mode
      this.clearCache(); 
    },
    downloadMultiCompetitorReportData() { // ADD this method
      if (!this.marketReportData) return;
      const dataStr = JSON.stringify(this.marketReportData, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      
      const exportFileDefaultName = `market_report_${this.selectedStore.name ? this.selectedStore.name.replace(/\s+/g, '_') : 'selected_store'}_${new Date().toISOString().split('T')[0]}.json`;
      
      let linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    },
    scrollToCategoryDetail(categoryKey) { // ADD this method for scrolling
      const element = this.$el.querySelector(`#category-detail-${categoryKey}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  },
  async mounted() {
    // Wait for store to be initialized before loading data
    if (this.isAuth && this.currentUID && this.WID) {
      await this.fetchStores();
      await this.loadHistoryFromFirestore();
    }
    
    this.$nextTick(() => {
      const checkGoogleMapsLoaded = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          console.log("Google Maps Places API loaded, initializing autocomplete");
          this.initAutocomplete();
        } else {
          console.log("Waiting for Google Maps Places API to load...");
          setTimeout(checkGoogleMapsLoaded, 500);
        }
      };
      checkGoogleMapsLoaded();
    });
    
    document.addEventListener('click', this.handleClickOutside);
    this.$root.$on('workspace-changed', this.resetOnWorkspaceChange);
  },
  beforeDestroy() {
    this.$root.$off('workspace-changed', this.resetOnWorkspaceChange);
    document.removeEventListener('click', this.handleClickOutside);
    
    // Clean up autocomplete
    if (this.autocomplete && window.google?.maps?.event) {
      window.google.maps.event.clearInstanceListeners(this.autocomplete);
      this.autocomplete = null;
    }
  },
  watch: {
    isAuth: {
      handler(newAuth, oldAuth) {
        if (newAuth && newAuth !== oldAuth && this.currentUID && this.WID) {
          // User is now authenticated, fetch data
          this.fetchStores();
          this.loadHistoryFromFirestore();
        }
      },
      immediate: true
    },
    currentUID: {
      handler(newUID, oldUID) {
        if (newUID && newUID !== oldUID && this.isAuth && this.WID) {
          // User ID is now available, fetch data
          this.fetchStores();
          this.loadHistoryFromFirestore();
        }
      },
      immediate: true
    },
    WID: {
      handler(newWID, oldWID) {
        if (newWID && newWID !== oldWID) {
          console.log("Workspace changed, reloading data...");
          if (this.isAuth && this.currentUID) {
            this.fetchStores();
            this.loadHistoryFromFirestore();
          }
        }
      },
      immediate: true
    },
    selectedStore(newStore, oldStore) {
      this.clearCache();
      if (newStore && newStore !== oldStore) {
        // Re-initialize autocomplete when store changes to update location biasing
        this.$nextTick(() => {
          this.initAutocomplete();
        });
      }
    },
    selectedCompetitor() {
      this.clearCache();
    }
  }
};
</script>

<style scoped>
/* Add styles to force full width */
:deep(.v-tabs) {
  width: 100% !important;
}

:deep(.v-tabs-items) {
  width: 100% !important;
}

:deep(.v-window__container) {
  width: 100% !important;
}

:deep(.v-window-item) {
  width: 100% !important;
}

/* Ensure tables take full width */
table {
  width: 100% !important;
  min-width: 100% !important;
}

/* Responsive padding and full-width for form and report */
.form-container {
  width: 100%;
  max-width: 100%; /* Changed from max-w-4xl */
  padding-left: 1rem; /* Added for spacing on smaller screens */
  padding-right: 1rem; /* Added for spacing on smaller screens */
}

.report-content-wrapper {
  width: 100%;
  max-width: 100%;
  padding-left: 1rem; /* Added for spacing on smaller screens */
  padding-right: 1rem; /* Added for spacing on smaller screens */
}

@media (min-width: 768px) { /* md breakpoint */
  .form-container {
    padding-left: 2rem;
    padding-right: 2rem;
  }
  .report-content-wrapper {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}

@media (min-width: 1024px) { /* lg breakpoint */
  .form-container {
    padding-left: 3rem; /* Adjusted for larger screens */
    padding-right: 3rem; /* Adjusted for larger screens */
  }
  .report-content-wrapper {
    padding-left: 3rem; /* Adjusted for larger screens */
    padding-right: 3rem; /* Adjusted for larger screens */
  }
}

/* Dropdown positioning and stacking context */
.relative {
  position: relative;
}

[ref="showStoreDropdown"] {
  position: absolute;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

/* Form container styles */
.v-menu__content {
  z-index: 51 !important;
}

/* Location input styles */
.location-input {
  width: 100% !important;
}

.location-input :deep(.v-input__slot) {
  background: white !important;
}

/* Platform item styles */
.platform {
  transition: all 0.2s ease;
}

.platform:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* Input field enhancements */
.v-text-field.v-text-field--outlined >>> fieldset {
  border-width: 1px !important;
  border-color: #e2e8f0 !important;
}

.v-text-field.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Button enhancements */
.v-btn.v-size--x-large {
  height: 56px !important;
  font-size: 1.1rem !important;
  letter-spacing: 0.5px !important;
  min-width: 150px; /* Ensure button has a decent minimum width */
}

/* Responsive table container */
.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Prevent text overflow */
.break-words {
  word-break: break-word;
}

/* Ensure consistent padding on mobile */
@media (max-width: 768px) {
  .p-4 {
    padding: 1rem !important;
  }
  .md\\:p-6 { /* Target specific md prefixed padding */
    padding: 1rem !important;
  }
  .md\\:p-8 { /* Target specific md prefixed padding */
    padding: 1.5rem !important; /* Slightly more padding for form on mobile */
  }
  .gap-4 {
    gap: 1rem !important;
  }
  .mb-8 {
    margin-bottom: 1.5rem !important;
  }
  .mb-12 {
    margin-bottom: 2rem !important;
  }
  .text-2xl {
    font-size: 1.25rem !important; /* Adjust heading size on mobile */
  }
}
</style> 